// eslint-disable-next-line import/no-extraneous-dependencies
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { Link, useHistory } from 'react-router-dom';
import {
  Card,
  Col,
  Container,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from 'reactstrap';
import { useBoolean } from 'usehooks-ts';

import Breadcrumb from 'components/App/Breadcrumb';
import Button from 'components/App/Button';
import AppInputValidation from 'components/App/InputValidation';
import Meta from 'components/App/Meta';
import AppSelectValidation from 'components/App/SelectValidation';
import ModalDetailReview from 'components/Modals/ModalDetailReview';
import ModalRequestAction from 'components/Modals/ModalRequestAction';
import Pagination from 'components/Pagination';

import images from 'assets/images';
import { themeColor } from 'constants/index';
import cn from 'utils/classnames';

import { DatePicker } from 'antd';
import DataTableReviews from 'components/App/DataTableReviews';
import omit from 'lodash/omit';
import moment from 'moment';
import { getParamsFormObject, showSuccess } from 'utils';
import ENDPOINT from 'constants/endPoint';
import styles from './ManagerDiscoveryReview.module.scss';

const breadcrumbList = [
  {
    name: 'Khám phá',
    isActive: false,
    link: '', // TODO: ADD ROUTE WHEN HAVE LINK
  },
  {
    name: 'Danh sách đánh giá',
    isActive: true,
    link: '', // TODO: ADD ROUTE WHEN HAVE LINK
  },
];

const TAB = {
  ALL: 'ALL',
  ACTIVE: 'ACTIVE',
  DELETED: 'DELETED',
};

const NAV_TAB = [
  {
    title: 'Tất cả',
    value: TAB.ALL,
  },
  {
    title: 'Đang hiển thị',
    value: TAB.ACTIVE,
  },
  {
    title: 'Đã xoá',
    value: TAB.DELETED,
  },
];

const DEFAULT_LIMIT = 20;

function ManagerDiscoveryReview(props) {
  const {
    // Discover Review
    reviews,
    reviewsLoading,
    actionGetDiscoveryReviews,
    actionCleanupDiscoverReviews,

    actionDeleteDiscoveryReview,
    isDeleteDiscoveryReviewLoading,

    // Discover Province
    provincesLoading,
    provinces,
    actionGetDiscoveryProvinces,
    actionCleanupDiscoveryProvinces,

    // Discover Category
    categoriesLoading,
    categories,
    actionGetDiscoveryCategories,
    actionCleanupDiscoveryCategories,
  } = props;

  const history = useHistory();

  const params = useMemo(() => new URLSearchParams(history.location.search),
    [history.location.search]);

  const searchQueries = useMemo(() => ({
    page: params.get('page') || 1,
    q: params.get('q') || '',
    limit: DEFAULT_LIMIT,
    place: params.get('place') || '',
    from: params.get('from') || '',
    to: params.get('to') || '',
    category: params.get('category') || '',
    province: params.get('province') || '',
    status: params.get('status') || TAB.ALL,
  }), [params]);

  const {
    value: isOpenModalDeleteLocation,
    setTrue: onOpenModalDeleteLocation,
    setFalse: onCloseModalDeleteLocation,
    toggle: onToggleModalDeleteLocation,
  } = useBoolean(false);

  const [locationDetail, setLocationDetail] = React.useState(null);

  const {
    value: isOpenModalDetail,
    setTrue: onOpenModalDetail,
    setFalse: onCloseModalDetail,
  } = useBoolean(false);

  const validation = useFormik({
    initialValues: {
      q: searchQueries.q,
      place: searchQueries.place,
      from: searchQueries.from,
      to: searchQueries.to,
      category: searchQueries.category,
      province: searchQueries.province,
      page: searchQueries.page,
      limit: DEFAULT_LIMIT,
      status: searchQueries.status,
    },
    onSubmit: (values) => {
      history.push({
        search: getParamsFormObject({
          ...values,
          page: 1,
        }),
      });
    },
  });

  const onChangeFilterOption = React.useCallback((e) => {
    validation.setFieldValue(e.name, e.value, true);
  }, [validation]);

  const onResetForm = React.useCallback(() => {
    const formValue = {
      q: '',
      place: '',
      category: '',
      star: '',
      from: '',
      to: '',
      page: 1,
      limit: DEFAULT_LIMIT,
      status: TAB.ALL,
    };

    validation.resetForm({
      values: { ...formValue },
    });

    history.push({
      search: getParamsFormObject({ ...formValue }),
    });
  }, [history, validation]);

  const isSearchBtnDisabled = React.useMemo(() => (
    Object
      .values(omit(validation.values, ['limit', 'page']))
      .filter(Boolean)
      .length === 0
    && validation.values.q === ''
  ), [validation.values]);

  const onChangeTab = React.useCallback((tab) => () => {
    validation.setFieldValue('status', tab, true);

    history.push({
      search: getParamsFormObject({
        ...searchQueries,
        status: tab,
        page: 1,
      }),
    });
  }, [history, searchQueries, validation]);

  const onChangePage = React.useCallback((selected) => {
    history.push({
      search: getParamsFormObject({
        ...searchQueries,
        page: selected.selected + 1,
      }),
    });
    // tableRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [history, searchQueries]);

  const totalPage = React.useMemo(() => (
    Math.ceil((reviews.total || 0) / DEFAULT_LIMIT)
  ), [reviews.total]);

  const onDeleteLocation = React.useCallback((locationId) => {
    const reviewFound = reviews.data.find((item) => item._id === locationId);
    setLocationDetail(reviewFound || null);
    onOpenModalDeleteLocation();
  }, [onOpenModalDeleteLocation, reviews.data]);

  const onConfirmDeleteLocation = React.useCallback(() => {
    actionDeleteDiscoveryReview({
      id: locationDetail._id,
      callback: () => {
        if (isOpenModalDetail) {
          onCloseModalDetail();
        }

        onCloseModalDeleteLocation();

        showSuccess(
          locationDetail.deletedAt ?
            'Khôi phục đánh giá thành công'
            : 'Xóa đánh giá thành công');
      },
    });
  }, [
    actionDeleteDiscoveryReview,
    isOpenModalDetail,
    locationDetail?._id,
    locationDetail?.deletedAt,
    onCloseModalDeleteLocation,
    onCloseModalDetail,
  ]);

  const onOpenDetailLocation = React.useCallback((locationId) => {
    const foundLocation = reviews.data.find((item) => item._id === locationId);
    setLocationDetail(foundLocation || null);
    onOpenModalDetail();
  }, [reviews, onOpenModalDetail]);

  const onSelectDate = useCallback((keyValue) => (_, dateString) => {
    validation.setFieldValue(keyValue, dateString, true);
  }, [validation]);

  React.useEffect(() => {
    actionGetDiscoveryReviews({
      page: searchQueries.page,
      limit: DEFAULT_LIMIT,
      q: searchQueries.q,
      province: searchQueries.province,
      category: searchQueries.category,
      star: searchQueries.star,
      from: searchQueries.from,
      to: searchQueries.to,
      status: searchQueries.status,
    });
  }, [
    actionGetDiscoveryReviews,
    searchQueries.category,
    searchQueries.from,
    searchQueries.page,
    searchQueries.province,
    searchQueries.q,
    searchQueries.star,
    searchQueries.status,
    searchQueries.to,
  ]);

  React.useEffect(() => {
    actionGetDiscoveryProvinces({
      page: 1,
      limit: 1000,
    });

    actionGetDiscoveryCategories({
      page: 1,
      limit: 1000,
    });

    return () => {
      actionCleanupDiscoveryProvinces();
      actionCleanupDiscoveryCategories();
      actionCleanupDiscoverReviews();
    };
  }, [
    actionCleanupDiscoveryProvinces,
    actionCleanupDiscoveryCategories,
    actionGetDiscoveryCategories,
    actionGetDiscoveryProvinces,
    actionCleanupDiscoverReviews,
  ]);

  return (
    <>
      <div className={`page-content ${styles.Page}`}>
        <Meta name="Danh sách đánh giá" />

        <Container fluid>
          {/* Render Breadcrumb */}
          <Breadcrumb
            pageTitle="Danh sách đánh giá"
            breadcrumbList={breadcrumbList}
          />

          <Card className="Section">
            <form onSubmit={validation.handleSubmit}>
              <Row>
                <Col xxl="6" className="mb-24">
                  <Label className="Section__Search__Label">
                    Tìm kiếm
                  </Label>

                  <div className="Section__Search__Container">
                    <AppInputValidation
                      name="q"
                      id="q"
                      type="text"
                      placeholder="Tìm theo tên user, điểm khám phá"
                      validation={validation}
                      inputClassName={cn('Section__Search__Input')}
                    />

                    <i className="fas fa-search Section__Search__Icon__Search" />
                  </div>
                </Col>

                <Col xxl="auto">
                  <Label className="Section__Search__Label">
                    Bộ lọc
                  </Label>

                  <Row>
                    <Col md="auto" className="mb-24">
                      <div style={{ minWidth: '100px' }}>
                        <AppSelectValidation
                          isDisabled={provincesLoading}
                          placeholder="Điểm đến"
                          name="province"
                          options={provinces.data.map((item) => ({
                            value: item._id,
                            label: item.name,
                          }))}
                          validation={validation}
                          value={validation.values.province}
                          onChange={onChangeFilterOption}
                          isHiddenSearchable
                          style={{ height: '39px' }}
                        />
                      </div>

                    </Col>

                    <Col md="auto" className="mb-24">
                      <div style={{ minWidth: '100px' }}>
                        <AppSelectValidation
                          placeholder="Hạng mục"
                          name="category"
                          options={categories.data.map((item) => ({
                            value: item._id,
                            label: item.name,
                          }))}
                          isDisabled={categoriesLoading}
                          validation={validation}
                          value={validation.values.category}
                          onChange={onChangeFilterOption}
                          isHiddenSearchable
                          style={{ height: '39px' }}
                        />
                      </div>
                    </Col>

                    <Col md="auto" className="mb-24">
                      <div style={{ minWidth: '100px' }}>
                        <AppSelectValidation
                          placeholder="Điểm đánh giá"
                          name="star"
                          options={[
                            {
                              value: 5,
                              label: '5 sao',
                            },
                            {
                              value: 4,
                              label: '4 sao',
                            },
                            {
                              value: 3,
                              label: '3 sao',
                            },
                            {
                              value: 2,
                              label: '2 sao',
                            },
                            {
                              value: 1,
                              label: '1 sao',
                            },
                          ]}
                          validation={validation}
                          value={validation.values.star}
                          onChange={onChangeFilterOption}
                          isHiddenSearchable
                          style={{ height: '39px' }}
                        />
                      </div>
                    </Col>
                  </Row>
                </Col>

                <Col xxl="auto">
                  <Label className="Section__Search__Label">
                    Thời gian
                  </Label>

                  <Row>
                    <Col md="auto" className="mb-24">
                      <DatePicker
                        value={
                          moment(validation.values.from).isValid()
                            ? dayjs(validation.values.from)
                            : undefined
                        }
                        className="Section__Search__DatePicker"
                        onChange={onSelectDate('from')}
                        placeholder="Từ ngày"
                        inputReadOnly
                        style={{ height: '39px' }}
                      />
                    </Col>

                    <Col md="auto" className="mb-24">
                      <DatePicker
                        value={
                          moment(validation.values.to).isValid()
                            ? dayjs(validation.values.to)
                            : undefined
                        }
                        className="Section__Search__DatePicker"
                        onChange={onSelectDate('to')}
                        placeholder="Đến ngày"
                        inputReadOnly
                        style={{ height: '39px' }}
                        disabledDate={(current) => current
                          && current.valueOf() < Date.parse(validation.values.from)}
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>

              <div className="d-flex">
                <Button
                  outline
                  classes="Section__Search__Button__ReType mr-24"
                  color={themeColor.LIGHT}
                  onClick={onResetForm}
                >
                  Nhập lại
                </Button>

                <Button
                  classes="Section__Search__Button__Search"
                  type="submit"
                  disabled={isSearchBtnDisabled}
                >
                  Tìm kiếm
                </Button>
              </div>
            </form>
          </Card>

          <Card className="Section">
            <Row>
              <div className="d-flex flex-column flex-md-row gap-4 justify-content-between custom_pills">
                <Nav pills>
                  {
                    NAV_TAB.map((tab) => (
                      <NavItem key={tab.value}>
                        <NavLink
                          className={
                            validation.values.status === tab.value
                              ? 'active'
                              : ''
                          }
                          onClick={onChangeTab(tab.value)}
                        >
                          <span>{tab.title}</span>
                        </NavLink>
                      </NavItem>
                    ))
                  }
                </Nav>

                <Link to={ENDPOINT.MANAGER_DISCOVERY_PLACE_CREATE}>
                  <Button
                    classes="Table__Header__Button mt-3 mt-lg-0"
                    fontSize="13px"
                  >
                    <i className="fas fa-plus" />
                    Thêm điểm khám phá
                  </Button>
                </Link>
              </div>
            </Row>

            <TabContent
              activeTab={validation.values.status}
              className="Table__Body"
            >
              <Label className="Table__Body__Total">
                {`${reviews.total} đánh giá`}
              </Label>

              {NAV_TAB.map((item) => (
                <TabPane
                  tabId={item.value}
                  key={item.value}
                >
                  <DataTableReviews
                    isLoadingLocationList={reviewsLoading}
                    locations={reviews.data}
                    onDeleteLocation={onDeleteLocation}
                    onOpenDetail={onOpenDetailLocation}
                  />
                </TabPane>
              ))}
            </TabContent>
          </Card>

          <Pagination
            page={Number(searchQueries.page)}
            pageCount={totalPage}
            onChangePage={onChangePage}
          />
        </Container>

        <ModalDetailReview
          isOpen={isOpenModalDetail}
          onCloseModal={onCloseModalDetail}
          locationDetail={locationDetail}
          onOpenDetailLocation={() => onDeleteLocation(locationDetail._id)}
          isDeleteDiscoveryReviewLoading={isDeleteDiscoveryReviewLoading}
        />
      </div>

      <ModalRequestAction
        isOpen={isOpenModalDeleteLocation}
        icon={images.modalGarbageCan.default}
        textRequest={`Bạn có chắc chắn muốn ${locationDetail?.deletedAt ? 'khôi phục' : 'xóa'} \n đánh giá này không?`}
        onToggle={onToggleModalDeleteLocation}
        onCloseModal={onCloseModalDeleteLocation}
        onConfirm={onConfirmDeleteLocation}
        isLoadingConfirm={isDeleteDiscoveryReviewLoading}
      />
    </>
  );
}

ManagerDiscoveryReview.propTypes = {
  reviews: PropTypes.instanceOf(Object).isRequired,
  reviewsLoading: PropTypes.bool.isRequired,
  actionGetDiscoveryReviews: PropTypes.func.isRequired,
  actionCleanupDiscoverReviews: PropTypes.func.isRequired,
  actionDeleteDiscoveryReview: PropTypes.func.isRequired,
  isDeleteDiscoveryReviewLoading: PropTypes.bool.isRequired,

  provincesLoading: PropTypes.bool.isRequired,
  provinces: PropTypes.instanceOf(Array).isRequired,
  actionGetDiscoveryProvinces: PropTypes.func.isRequired,
  actionCleanupDiscoveryProvinces: PropTypes.func.isRequired,

  categoriesLoading: PropTypes.bool.isRequired,
  categories: PropTypes.instanceOf(Array).isRequired,
  actionGetDiscoveryCategories: PropTypes.func.isRequired,
  actionCleanupDiscoveryCategories: PropTypes.func.isRequired,
};

ManagerDiscoveryReview.defaultProps = {
};

export default ManagerDiscoveryReview;
